import { __awaiter } from "tslib";
import { logActivity } from '@lusha/idle-management';
import { getPlatformInfo } from '@lusha-parser/core';
import parser from '@lusha-parser/parse';
import { getParserByPlatform } from '@lusha-parser/parse/src/index';
import { actions as bulkActions } from '@reducers/bulk/reducer';
import { actions as companyActions } from '@reducers/company/reducer';
import ContactDispatcher from '@reducers/contact/dispatcher';
import { selectCurrentContact } from '@reducers/contact/selectors';
import { selectIsEmpty } from '@reducers/contacts/selectors';
import { actions as searchCompanyActions } from '@reducers/searchCompany/reducer';
import SettingsDispatcher from '@reducers/settings/dispatcher';
import { selectIsAutoOpenEnabled } from '@reducers/settings/selectors';
import { selectIsIdleLogoutEnabled } from '@reducers/user/selectors';
import { getType, PAGE_TYPES, shouldPreventSearchesOnSubViews } from '@utils/pageType';
import { communicator, dismissAllPopups } from 'lusha-widgets';
import qs from 'qs';
import { dismissNotifications } from '../../hooks/useNotification';
import appActions from './actions';
import { actions } from './reducer';
import { selectIsBulkPage, selectIsCompanyPage } from './selectors';
import { APP_CONTEXT } from './types';
class Dispatcher {
    constructor() {
        this.actions = actions;
    }
    install() {
        return (dispatch) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const query = qs.parse(((_a = document.location.search) === null || _a === void 0 ? void 0 : _a.replace('?', '')) || '');
            if (query.app) {
                dispatch(actions.setApp(query.app));
            }
            dispatch(this.installLazy());
        });
    }
    installLazy() {
        return (dispatch) => __awaiter(this, void 0, void 0, function* () { });
    }
    setPage(page) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const url = page === null || page === void 0 ? void 0 : page.url;
            if (!url)
                return null;
            const previousUrl = getState().app.url;
            const isNewPage = previousUrl !== url;
            dispatch(actions.setUrl(url));
            const platformInfo = getPlatformInfo(url);
            if (platformInfo) {
                dispatch(SettingsDispatcher.actions.set({ platform: platformInfo.name }));
            }
            // automatically assign scout info. no parser needed
            if ((platformInfo === null || platformInfo === void 0 ? void 0 : platformInfo.name) === 'scout') {
                const result = yield parser.parse({ url, platform: 'scout' });
                dispatch(actions.setParserResult(result));
            }
            if (isNewPage) {
                dispatch(this.onPageUrlChanged());
                dispatch(this.onPageTypeChanged(page, previousUrl));
                const platformParser = getParserByPlatform(platformInfo === null || platformInfo === void 0 ? void 0 : platformInfo.name);
                if (!platformParser) {
                    // parser always exists. just add next line to fix ai
                    dispatch(actions.setIsParserExistsForPage(false));
                    return true;
                }
                const isParserExists = !!((_a = platformParser.isParserExistsForPage) === null || _a === void 0 ? void 0 : _a.call(platformParser, url));
                dispatch(actions.setIsParserExistsForPage(isParserExists));
            }
        });
    }
    /**
     * Moved from MST `Context.setPage`. Re-derives the page type and runs the per-type search
     * resets.
     */
    onPageTypeChanged(page, previousUrl) {
        return (dispatch, getState) => {
            const state = getState();
            const previousPageType = state.app.pageType;
            if (selectIsIdleLogoutEnabled(state.user)) {
                logActivity();
            }
            const wasProfileOrLinkedinCompany = previousPageType === PAGE_TYPES.PROFILE || previousPageType === PAGE_TYPES.LINKEDIN_COMPANY;
            if (wasProfileOrLinkedinCompany && shouldPreventSearchesOnSubViews(page, previousUrl)) {
                return;
            }
            const wasBulk = previousPageType === PAGE_TYPES.BULK;
            dispatch(actions.setPageType(getType(Object.assign({ corePluginVersion: state.settings.version }, page))));
            const nextState = getState();
            const isBulk = selectIsBulkPage(nextState);
            if (isBulk) {
                dispatch(bulkActions.setCreditSplitClickedButtonType('show'));
                dispatch(searchCompanyActions.reset({ context: nextState.app.appContext }));
            }
            if (selectIsCompanyPage(nextState) || (wasBulk && !isBulk)) {
                dispatch(searchCompanyActions.reset({ context: APP_CONTEXT.PROSPECT }));
            }
        };
    }
    onPageUrlChanged() {
        return (dispatch, getState) => {
            const state = getState();
            dismissAllPopups();
            dismissNotifications();
            const currentContact = selectCurrentContact(state);
            const isAutoOpenEnabled = selectIsAutoOpenEnabled(state);
            if (currentContact &&
                !selectIsEmpty(currentContact) &&
                state.app.appRoute === 'lusha-contacts' &&
                isAutoOpenEnabled) {
                const { postMessage: openFrame } = communicator.getMessenger('OPEN');
                openFrame({ openType: 'autoOpen' });
            }
            dispatch(appActions.onPageChanged());
            dispatch(ContactDispatcher.clearSearchResult());
            dispatch(companyActions.clearSearchEmployeesResult());
        };
    }
}
const AppDispatcher = new Dispatcher();
export default AppDispatcher;
